'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InsideArrows } from './components/arrows/InsideArrows/InsideArrows';
import { OutsideArrows } from './components/arrows/OutsideArrows/OutsideArrows';
import { Slider } from './components/Slider/Slider';
import {
  ScArrowProps,
  SlideGrid,
  SliderArrow,
  SliderWithArrowProps,
} from './helpers';
import { debounce, delay, groupItems } from '@/helpers/common';
import { MediaType } from '@/helpers/enums';
import { useChunked } from '@/hooks/chunkedSlides';
import useEventListener from '@/hooks/eventListener';
import { useCurrentMediaType } from '@/hooks/responsive';
import { useCommonStore } from '@/store';

import { ScSliderProps } from './components/Slider/styled';
import { ScSliderContainer } from './styled';

export type SliderHelpers = Partial<{
  onScrollToStart: () => void;
}>;

interface SliderProps<T> extends ScArrowProps, Omit<ScSliderProps, 'padding'> {
  pending?: boolean;
  pendingSlide?: React.ComponentType<{}>;
  slides: T[];
  slideGrid?: SlideGrid;
  slide: React.ComponentType<T & SliderHelpers>;
  arrows?: SliderWithArrowProps;
  lazyLoadingOf?: number;
  autoplay?: {
    delay: number;
    pauseOnMouseEnter?: boolean;
  };
  loop?: boolean;
  pagination?: boolean;
  paginationInset?: string;
  initialSlide?: number;
}

export function NativeSlider<T>({
  slides,
  slide: Slide,
  arrows,
  loop,
  autoplay,
  pagination,
  initialSlide,
  slideGrid,
  lazyLoadingOf,
  paginationInset,
  ...props
}: SliderProps<T>) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [isBeginning, setIsBeginning] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [hideArrows, setHideArrows] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const currentMediaType = useCurrentMediaType();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { cols = 1, rows = 1 } = useMemo(() => {
    switch (currentMediaType) {
      case MediaType.DESKTOP_L:
        return {
          cols: slideGrid?.desktopL?.cols,
          rows: slideGrid?.desktopL?.rows,
        };
      case MediaType.DESKTOP:
        return {
          cols: slideGrid?.desktop?.cols,
          rows: slideGrid?.desktop?.rows,
        };
      case MediaType.TABLET:
        return {
          cols: slideGrid?.tablet?.cols,
          rows: slideGrid?.tablet?.rows,
        };
      case MediaType.MOBILE:
        return {
          cols: slideGrid?.mobile?.cols,
          rows: slideGrid?.mobile?.rows,
        };
      case MediaType.MOBILE_S:
        return {
          cols: slideGrid?.mobileS?.cols,
          rows: slideGrid?.mobileS?.rows,
        };
      default:
        return {
          cols: 1,
          rows: 1,
        };
    }
  }, [currentMediaType, slideGrid]);

  const gridSlides = useMemo(
    () => groupItems(slides, rows, cols),
    [cols, rows, slides]
  );

  const [isEnd, setIsEnd] = useState(gridSlides.length === 1);

  const isOne = useMemo(
    () => gridSlides == null || gridSlides.length <= 1,
    [gridSlides]
  );

  const handleArrows = useCallback(async () => {
    if (hideArrows) return;
    setHideArrows(true);
    await delay(500);
    setHideArrows(false);
  }, [hideArrows]);

  const handleScrollTo = useCallback((scrollLeft: number) => {
    sliderRef.current?.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, []);

  const handleScrollToStart = useCallback(() => {
    handleScrollTo(0);
  }, [handleScrollTo]);

  const handlePrevClick = useCallback(() => {
    const track = sliderRef.current;
    if (track) {
      handleArrows();

      const scrollTarget =
        track.scrollLeft <= 0 && loop
          ? track.scrollWidth - track.clientWidth
          : track.scrollLeft - slideWidth;

      handleScrollTo(scrollTarget);
    }
  }, [handleArrows, loop, handleScrollTo, slideWidth]);

  const handleNextClick = useCallback(() => {
    const track = sliderRef.current;

    if (track == null) return;

    handleArrows();

    const scrollTarget =
      track.scrollLeft >= track.scrollWidth - track.clientWidth && loop
        ? 0
        : track.scrollLeft + slideWidth;

    handleScrollTo(scrollTarget);
  }, [handleArrows, loop, handleScrollTo, slideWidth]);

  const handleScroll = debounce(() => {
    const track = sliderRef.current;
    if (track == null) return;

    const index = Math.round(track.scrollLeft / slideWidth);

    setCurrentSlideIndex(index);
    setIsBeginning(index === 0 && !loop);
    setIsEnd(index === gridSlides.length - 1 && !loop);
  }, 100);

  useEffect(() => {
    if (!isHovering && autoplay?.pauseOnMouseEnter) {
      const autoScrollInterval = setInterval(() => {
        handleNextClick();
      }, autoplay.delay);

      return () => {
        clearInterval(autoScrollInterval);
      };
    }
  }, [autoplay, handleNextClick, isHovering]);

  useEffect(() => {
    initialSlide && handleScrollTo(initialSlide * slideWidth);
  }, [handleScrollTo, initialSlide, slideWidth]);

  const perChunk = useMemo(() => {
    if (lazyLoadingOf == null || props?.slidesPerView == null)
      return gridSlides?.length || 0;

    return Math.floor(props.slidesPerView) * lazyLoadingOf;
  }, [lazyLoadingOf, props.slidesPerView, gridSlides?.length]);

  const { itemsChunk: slidesChunk, nextChunk } = useChunked({
    items: gridSlides,
    perChunk,
  });

  const handleNativeScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(function () {
        const target = event.target as HTMLDivElement;

        const scrollOffset =
          target.scrollWidth - target.clientWidth - target.scrollLeft;
        const perChunkOffset = target.scrollWidth / perChunk;

        const shouldLoadNextChunk = scrollOffset < perChunkOffset;

        if (shouldLoadNextChunk) {
          nextChunk();
        }
      }, 150);
    },
    [nextChunk, perChunk]
  );

  useEventListener('scroll', handleScroll, sliderRef);

  return gridSlides ? (
    <ScSliderContainer
      onMouseEnter={
        autoplay?.pauseOnMouseEnter ? () => setIsHovering(true) : undefined
      }
      onMouseLeave={
        autoplay?.pauseOnMouseEnter ? () => setIsHovering(false) : undefined
      }
      ref={(node: HTMLDivElement | null) => {
        if (node) {
          setSlideWidth(node.clientWidth);
        }
      }}
      isFullScreen={isMobileDevice}
      paginationInset={paginationInset}
    >
      {arrows?.type === SliderArrow.OUTSIDE ? (
        <OutsideArrows
          rightComponent={arrows.componentRight}
          leftComponent={arrows.componentLeft}
          visibleForMobile={arrows.visibleForMobile}
          isBeginning={isBeginning}
          isEnd={isEnd}
          dark={arrows.dark}
          handleNextClick={handleNextClick}
          handlePrevClick={handlePrevClick}
        />
      ) : null}

      {/* inside arrows */}
      {arrows?.type === SliderArrow.INSIDE && !isOne && (
        <InsideArrows
          visibleForMobile={arrows.visibleForMobile}
          isBeginning={isBeginning}
          isEnd={isEnd}
          handlePrevClick={handlePrevClick}
          handleNextClick={handleNextClick}
          hideArrows={hideArrows}
          insetLeftArrow={arrows.insetLeftArrow}
          insetRightArrow={arrows.insetRightArrow}
          loop={loop}
        />
      )}
      <Slider
        itemComponent={Slide}
        currentSlideIndex={currentSlideIndex}
        handleNativeScroll={handleNativeScroll}
        slidesPerView={props.slidesPerView}
        sliderRef={sliderRef}
        slidesChunk={slidesChunk}
        sliderHelpers={{
          onScrollToStart: handleScrollToStart,
        }}
      />

      {/* pagination */}
      {pagination && (
        <div className="pagination">
          {slidesChunk.map((_, index) => (
            <div
              key={index}
              className={`pagination-bullet ${
                index === currentSlideIndex ? 'active' : ''
              }`}
              onClick={() => handleScrollTo(index * slideWidth)}
            />
          ))}
        </div>
      )}
    </ScSliderContainer>
  ) : null;
}
